import { useMemo, useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Calendar, Download, Eye } from "lucide-react";
import { Pagination } from "antd";
import { Button } from "../../components/ui/button";
import { downloadCSV } from "@/lib/downloadCSV";
import { notify } from "@/components/common/Notification";
import { PtsPurchaseApprovalService } from "@/services/APIs";
import { Card, CardContent } from "../../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import PurchaseApprovalFilters from "./PurchaseApprovalFilters";
import {
  EMPTY_PURCHASE_APPROVAL_FILTERS,
  type PurchaseApprovalFilterValues,
} from "./PurchaseApprovalFilters.constants";
import { PurchaseApprovalStatusBadge } from "./StatusBadge";
import {
  formatDateYmd,
  formatInr,
  formatMoney,
  formatPurchaseApprovalReason,
  monthLabel,
  monthName,
  resolveName,
  purchaseStatusBadgeClass,
} from "./utils";
import { formatDateDmy } from "../LeaveRequests/utils";
import ReasonModal from "./ReasonModal";
import ReasonCell from "./ReasonCell";
import PurchaseApprovalViewModal from "./PurchaseApprovalViewModal";

type AccountantTabProps = {
  projectNameById: Map<string, string>;
  employeeNameById: Map<string, string>;
};

const AccountantTab = ({
  projectNameById,
  employeeNameById,
}: AccountantTabProps) => {
  const queryClient = useQueryClient();
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [monthIndex, setMonthIndex] = useState(() => new Date().getMonth());
  const [filterValues, setFilterValues] =
    useState<PurchaseApprovalFilterValues>(EMPTY_PURCHASE_APPROVAL_FILTERS);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [notPurchasedTargetId, setNotPurchasedTargetId] = useState<
    string | null
  >(null);
  const [viewId, setViewId] = useState<string | null>(null);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterValues, year, monthIndex]);

  const monthOptions = useMemo(() => {
    const options: {
      value: string;
      label: string;
      year: number;
      month: number;
    }[] = [];
    const base = new Date();
    for (let offset = 0; offset < 18; offset += 1) {
      const date = new Date(base.getFullYear(), base.getMonth() - offset, 1);
      options.push({
        value: `${date.getFullYear()}-${date.getMonth()}`,
        label: monthLabel(date.getFullYear(), date.getMonth()),
        year: date.getFullYear(),
        month: date.getMonth(),
      });
    }
    return options;
  }, []);

  const selectedMonth = monthName(monthIndex);

  // Query for approved purchase approvals
  const { data, isLoading } = useQuery({
    queryKey: [
      "purchase-approvals",
      "accountant-list",
      year,
      monthIndex,
      filterValues.projectId,
      filterValues.purchaseStatus,
      currentPage,
      pageSize,
    ],
    queryFn: () =>
      PtsPurchaseApprovalService.getPurchaseApprovals({
        page: currentPage,
        page_size: pageSize,
        month: selectedMonth,
        year,
        project: filterValues.projectId,
        status: "Approved", // Accountant works on approved ones
        purchase_status: filterValues.purchaseStatus,
      }),
  });

  const monthEntries = data?.results ?? [];
  const totalCount = data?.count ?? 0;
  const cards = data?.account_cards ?? {
    approved_requests_this_month: 0,
    total_cost_this_month: "0.00",
    purchased: 0,
    not_yet_purchased: 0,
  };

  const purchaseStatusMutation = useMutation({
    mutationFn: ({
      id,
      purchase_status,
      account_reason,
    }: {
      id: string;
      purchase_status: string;
      account_reason?: string;
    }) =>
      PtsPurchaseApprovalService.updatePurchaseApproval(id, {
        purchase_status,
        ...(account_reason ? { account_reason } : {}),
      }),
    onSuccess: result => {
      if (!result?.id) {
        return;
      }
      notify.success("Purchase status updated");
      setNotPurchasedTargetId(null);
      queryClient.invalidateQueries({ queryKey: ["purchase-approvals"] });
    },
  });

  const handlePurchaseStatusChange = (
    id: string,
    purchase_status: string,
    currentStatus: string
  ) => {
    if (purchase_status === currentStatus) {
      return;
    }
    if (purchase_status === "Not purchased") {
      setNotPurchasedTargetId(id);
      return;
    }
    purchaseStatusMutation.mutate({ id, purchase_status });
  };

  const handleExportEntriesCSV = async () => {
    try {
      const res = await PtsPurchaseApprovalService.getPurchaseApprovals({
        page_size: 100000,
        month: selectedMonth,
        year,
        project: filterValues.projectId,
        status: "Approved",
        purchase_status: filterValues.purchaseStatus,
      });

      const items = res.results;

      const escapeCsv = (val: any) => {
        const str = val === null || val === undefined ? "" : String(val);
        if (
          str.includes(",") ||
          str.includes('"') ||
          str.includes("\n") ||
          str.includes("\r")
        ) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      };

      const headers = [
        "ID",
        "PR Number",
        "Project ID",
        "Project Name",
        "Title",
        "Priority",
        "Requested Date",
        "Expected Date",
        "Cost",
        "Currency",
        "Frequency",
        "Requested By ID",
        "Requested By Name",
        "Description",
        "Status",
        "Purchase Status",
        "Reason",
        "Created On",
        "Updated On",
      ];

      const rows = items.map(item => {
        const projName =
          item.project_name ?? resolveName(item.project, projectNameById);
        const reqName = resolveName(item.requested_by, employeeNameById);

        return [
          escapeCsv(item.id),
          escapeCsv(item.pr_number),
          escapeCsv(item.project),
          escapeCsv(projName),
          escapeCsv(item.title),
          escapeCsv(item.priority),
          escapeCsv(formatDateDmy(item.requested_date)),
          escapeCsv(formatDateDmy(item.expected_date)),
          escapeCsv(item.cost),
          escapeCsv(item.currency ?? "INR"),
          escapeCsv(item.frequency),
          escapeCsv(item.requested_by),
          escapeCsv(reqName),
          escapeCsv(item.description),
          escapeCsv(item.status),
          escapeCsv(item.purchase_status),
          escapeCsv(formatPurchaseApprovalReason(item)),
          escapeCsv(formatDateYmd(item.created_on)),
          escapeCsv(formatDateYmd(item.updated_on)),
        ];
      });

      const csvContent = [
        headers.join(","),
        ...rows.map(r => r.join(",")),
      ].join("\n");

      downloadCSV(
        csvContent,
        `purchase-approvals-entries-${selectedMonth}-${year}`
      );
    } catch {
      notify.error("Failed to export CSV");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-xl font-bold text-[#111827] md:text-2xl">
          Accountant / IT dashboard
        </h1>
        <div className="w-full sm:w-56">
          <Select
            value={`${year}-${monthIndex}`}
            onValueChange={value => {
              const [y, m] = value.split("-").map(Number);
              setYear(y);
              setMonthIndex(m);
            }}
          >
            <SelectTrigger className="h-10 bg-white">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-gray-400" />
                <SelectValue placeholder="Month" />
              </div>
            </SelectTrigger>
            <SelectContent className="bg-white">
              {monthOptions.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <Card className="border-gray-200 bg-white shadow-sm">
          <CardContent className="p-5">
            <p className="text-sm text-gray-500">
              Approved requests this month
            </p>
            <p className="mt-2 text-3xl font-bold text-[#111827]">
              {cards.approved_requests_this_month}
            </p>
          </CardContent>
        </Card>
        <Card className="border-gray-200 bg-white shadow-sm">
          <CardContent className="p-5">
            <p className="text-sm text-gray-500">Total cost this month</p>
            <p className="mt-2 text-3xl font-bold text-[#111827]">
              {formatInr(cards.total_cost_this_month)}
            </p>
          </CardContent>
        </Card>
        <Card className="border-gray-200 bg-white shadow-sm">
          <CardContent className="p-5">
            <p className="text-sm text-emerald-700/80">Purchased</p>
            <p className="mt-2 text-3xl font-bold text-emerald-600">
              {cards.purchased}
            </p>
          </CardContent>
        </Card>
        <Card className="border-gray-200 bg-white shadow-sm">
          <CardContent className="p-5">
            <p className="text-sm text-rose-700/80">Not yet purchased</p>
            <p className="mt-2 text-3xl font-bold text-rose-600">
              {cards.not_yet_purchased}
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h2 className="text-xs font-medium uppercase tracking-wide text-gray-400">
            Purchase approval entries (Approved Only)
          </h2>
          <Button
            type="button"
            variant="default"
            size="sm"
            onClick={handleExportEntriesCSV}
            className="h-8 px-3 text-xs"
          >
            <Download className="mr-1.5 h-3.5 w-3.5" />
            Export Entries
          </Button>
        </div>
        <PurchaseApprovalFilters
          values={filterValues}
          onChange={setFilterValues}
          hideStatusFilter
          idPrefix="accountant-purchase-approval"
        />
        <Card className="border-gray-200 bg-white shadow-sm">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1200px] text-left text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-xs uppercase tracking-wide text-gray-400">
                    <th className="px-5 py-3 font-medium">PR Number</th>
                    <th className="px-5 py-3 font-medium">Project</th>
                    <th className="px-5 py-3 font-medium">Title</th>
                    <th className="px-5 py-3 font-medium">Cost</th>
                    <th className="px-5 py-3 font-medium">Frequency</th>
                    <th className="px-5 py-3 font-medium">Status</th>
                    <th className="px-5 py-3 font-medium">Requested Date</th>
                    <th className="px-5 py-3 font-medium">Expected Date</th>
                    <th className="px-5 py-3 font-medium">Requested By</th>
                    <th className="px-5 py-3 font-medium">Purchase Status</th>
                    <th className="w-[88px] px-5 py-3 text-center font-medium">
                      Reason
                    </th>
                    <th className="px-5 py-3 text-right font-medium">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td
                        colSpan={12}
                        className="px-5 py-10 text-center text-gray-500"
                      >
                        Loading...
                      </td>
                    </tr>
                  ) : monthEntries.length === 0 ? (
                    <tr>
                      <td
                        colSpan={12}
                        className="px-5 py-10 text-center text-gray-500"
                      >
                        No approved purchase requests found.
                      </td>
                    </tr>
                  ) : (
                    monthEntries.map(item => {
                      const isUpdating =
                        purchaseStatusMutation.isPending &&
                        purchaseStatusMutation.variables?.id === item.id;

                      return (
                        <tr
                          key={item.id}
                          className="border-b border-gray-50 last:border-0 hover:bg-slate-50/50"
                        >
                          <td className="px-5 py-3.5 font-medium text-[#111827]">
                            {item.pr_number || "—"}
                          </td>
                          <td className="px-5 py-3.5 text-[#374151]">
                            {item.project_name ??
                              resolveName(item.project, projectNameById)}
                          </td>
                          <td className="px-5 py-3.5 text-[#374151]">
                            {item.title || "—"}
                          </td>
                          <td className="px-5 py-3.5 font-semibold text-[#374151]">
                            {formatMoney(item.cost, item.currency)}
                          </td>
                          <td className="px-5 py-3.5 text-[#374151]">
                            {item.frequency || "—"}
                          </td>
                          <td className="px-5 py-3.5">
                            <PurchaseApprovalStatusBadge status={item.status} />
                          </td>
                          <td className="px-5 py-3.5 text-[#374151]">
                            {formatDateDmy(item.requested_date)}
                          </td>
                          <td className="px-5 py-3.5 text-[#374151]">
                            {formatDateDmy(item.expected_date)}
                          </td>
                          <td className="px-5 py-3.5 text-[#374151]">
                            {resolveName(item.requested_by, employeeNameById)}
                          </td>
                          <td className="px-5 py-3.5">
                            <Select
                              value={item.purchase_status || "Not purchased"}
                              disabled={isUpdating}
                              onValueChange={value =>
                                handlePurchaseStatusChange(
                                  item.id,
                                  value,
                                  item.purchase_status || "Not purchased"
                                )
                              }
                            >
                              <SelectTrigger
                                className={`h-8 w-[140px] rounded-2xl border-0 text-xs font-semibold ${purchaseStatusBadgeClass(
                                  item.purchase_status
                                )}`}
                              >
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-white">
                                <SelectItem value="In-progress">
                                  In-progress
                                </SelectItem>
                                <SelectItem value="Not purchased">
                                  Not purchased
                                </SelectItem>
                                <SelectItem value="Purchased">
                                  Purchased
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-5 py-3.5 text-center">
                            <ReasonCell item={item} />
                          </td>
                          <td className="px-5 py-3.5 text-center">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => setViewId(item.id)}
                              className="h-8 w-8 text-gray-600 hover:text-[#111827]"
                              aria-label="View purchase request"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
      {totalCount > 0 && (
        <div className="flex items-center justify-end p-4">
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={totalCount}
            onChange={(page, size) => {
              if (size && size !== pageSize) {
                setPageSize(size);
                setCurrentPage(1);
                return;
              }
              setCurrentPage(page);
            }}
            onShowSizeChange={(_current, size) => {
              setPageSize(size);
              setCurrentPage(1);
            }}
            showSizeChanger
            showQuickJumper
            showTotal={(total, range) =>
              `${range[0]}-${range[1]} of ${total} items`
            }
            pageSizeOptions={["50", "100", "200", "500"]}
            defaultPageSize={100}
            disabled={isLoading}
          />
        </div>
      )}

      <ReasonModal
        open={Boolean(notPurchasedTargetId)}
        title="Mark as not purchased"
        description="Please provide a reason for marking this request as not purchased."
        isSubmitting={purchaseStatusMutation.isPending}
        onOpenChange={open => {
          if (!open && !purchaseStatusMutation.isPending) {
            setNotPurchasedTargetId(null);
          }
        }}
        onSubmit={reason => {
          if (!notPurchasedTargetId) {
            return;
          }
          purchaseStatusMutation.mutate({
            id: notPurchasedTargetId,
            purchase_status: "Not purchased",
            account_reason: reason,
          });
        }}
      />

      <PurchaseApprovalViewModal
        id={viewId}
        open={Boolean(viewId)}
        projectNameById={projectNameById}
        employeeNameById={employeeNameById}
        onOpenChange={open => {
          if (!open) {
            setViewId(null);
          }
        }}
      />
    </div>
  );
};

export default AccountantTab;
